import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import pluralize from 'pluralize';

import { api } from '../../../services/api';
import { buildCountDescription, buildQueryFilter } from '../../../utils/utils';
import { dateService } from '../../../services/date';
import { formatCellValue, formatNumber } from '../../base/common/utils';
import { STRATEGY_GROUP_TYPE_LABELS } from '../../../utils/constants';

import { Table } from '../../ui/table';
import Column from '../../ui/table/column';
import Pagination from '../../base/common/pagination';
import Button from '../../ui/button';
import DropdownToggleWrapper from '../../ui/dropdown/dropdown-toggle-wrapper';
import Search from '../../ui/search/search';
import DropdownSelect from '../../ui/dropdown';
import StrategyStatus from '../../ui/strategy-status';
import PacingStatus from '../../ui/pacing-status/pacing-status';
import { Loader, NoResult } from '../../misc/loader-utils';
import DatePicker from '../../base/date/date-picker';
import ReportDropdown from '../../base/dropdowns/report-dropdown';
import PublisherDropdown from '../../base/dropdowns/v2/publisher-dropdown';
import MerchantStrategyTotals from '../campaign-tracking/merchant-strategy-totals';

import './campaign-center.scss';

const defaultDateParams = {
  startDate: dateService.getStartOf(null, 'month'),
  endDate: dateService.getCurrentDateTime(),
};

const statusDropdownItems = [
  { label: 'Completed', value: 3 },
  { label: 'Upcoming', value: 2 },
  { label: 'Paused', value: 4 },
  { label: 'Live', value: 1 },
];

const statType = 'campaign-center-campaigns-tab';

const CampaignsTab = props => {
  const { merchantId, topBarData } = props;
  const history = useHistory();
  const [tableData, setTableData] = useState([]);
  const [totalsData, setTotalsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [dateRange, setDateRange] = useState(defaultDateParams);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedPubs, setSelectedPubs] = useState([]);
  const [selectedBudgetType, setSelectedBudgetType] = useState([]);
  const isFiltered = !!searchQuery || statusFilter.length;

  // TODO: Can we move this defaultColumns definition out of this section? Needs access to component scope to utilize onOverflowSelect function
  const columns = [
    {
      key: 'overflow',
      dataKey: 'strategy_group_id',
      flexWidth: true,
      width: 50,
      cellRender: ({ cellData, rowIndex }) => {
        let menuAlign = 'left';

        if (perPage - rowIndex <= 2) {
          menuAlign += 'top';
        }

        return (
          <DropdownToggleWrapper
            items={[{ label: 'Edit', value: 'edit' }]}
            onSelect={option => onOverflowSelect(option.value, cellData)}
            menuAlign={menuAlign}
          >
            <Button kind="text" iconOnly="more" />
          </DropdownToggleWrapper>
        );
      },
    },
    {
      dataKey: 'strategy_group_status',
      label: 'Status',
      cellRender: ({ cellData }) => (
        <StrategyStatus status={cellData} enumKeys={true} styleType="normal" />
      ),
    },
    {
      dataKey: 'strategy_group_name',
      label: 'Budget Name',
      width: 350,
      flexWidth: true,
      cellRender: ({ cellData }) => <div style={{ whiteSpace: 'normal' }}>{cellData}</div>,
    },
    {
      dataKey: 'strategy_group_id',
      width: 50,
      totalRender: () => {
        return null;
      },
      cellRender: ({ cellData }) => (
        <Link to={`campaigns?strategy_group_ids=${cellData}`}>
          <Button kind="text" iconOnly="linkIn" />
        </Link>
      ),
    },
    {
      dataKey: 'strategy_group_type',
      label: 'Type',
      cellRender: ({ cellData }) => {
        if (!cellData) return '-';
        return <div style={{ whiteSpace: 'normal' }}>{STRATEGY_GROUP_TYPE_LABELS[cellData]}</div>;
      },
    },
    {
      dataKey: 'strategy_group_budget_amount',
      label: 'Budget Pool',
      format: { type: 'currency', options: { precision: 0 } },
    },
    {
      dataKey: 'merchant_spend',
      label: 'Spend',
      format: { type: 'currency', options: { precision: 0 } },
    },
    {
      dataKey: 'merchant_spend_pacing',
      width: 150,
      label: 'Budget Pacing',
      justifyContent: 'left',
      format: { type: 'currency', options: { precision: 0 } },
      cellRender: ({ cellData, rowData }) => {
        // TODO: Move this into a generic wrapper function
        if (typeof cellData === 'undefined' || cellData === null) {
          return '-';
        }

        const pacingIndicator = PacingStatus({
          value: rowData.merchant_spend
            ? parseFloat(rowData.merchant_spend_pacing) / parseFloat(rowData.merchant_spend)
            : 0,
          size: 11,
        });

        return (
          <div className="pacing-cell">
            <div style={{ color: pacingIndicator.props.color }}>
              {formatCellValue(rowData.merchant_spend_pacing, 'currency', {
                options: { precision: 0 },
              })}
            </div>
            {pacingIndicator}
          </div>
        );
      },
    },
    {
      dataKey: 'attributed_revenue',
      label: 'Revenue',
      format: { type: 'currency', options: { precision: 0 } },
    },
    { dataKey: 'impressions', label: 'Impressions', format: { options: { precision: 0 } } },
    { dataKey: 'clicks', label: 'Clicks', format: { options: { precision: 0 } } },
    {
      dataKey: 'strategy_group_number_of_strategies',
      label: 'Campaigns',
      format: { options: { precision: 0 } },
    },
    {
      dataKey: 'strategy_group_datetime_start',
      label: 'Start Date',
      width: 150,
      format: { type: 'date', options: { dateFormat: 'M/DD/YYYY' } },
      justifyContent: 'right',
      cellRender: ({ cellData, rowData }) => {
        if (rowData.strategy_group_is_default) {
          return 'Ongoing';
        }

        if (!cellData) return null;
        const daysPassed = Math.floor(
          dateService.subtractDatesV2(
            dateService.getStartOfDay(moment()),
            moment(cellData).utc(),
            'day',
          ) - 1,
        );

        const strategyGroupStatus = rowData.strategy_group_status;

        return (
          <div className="date-info-cell">
            <div>{cellData}</div>
            {(strategyGroupStatus === 1 || strategyGroupStatus === 2) && (
              <div className="date-sub-label">
                {daysPassed
                  ? daysPassed > 0
                    ? `${daysPassed} ${pluralize('day', daysPassed)} passed`
                    : `Starting in ${Math.abs(daysPassed)} days`
                  : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      dataKey: 'strategy_group_datetime_end',
      label: 'End Date',
      width: 150,
      format: { type: 'date', options: { dateFormat: 'M/DD/YYYY' } },
      justifyContent: 'right',
      cellRender: ({ cellData, rowData }) => {
        if (!cellData) return 'No End Date';
        const strategyGroupStatus = rowData.strategy_group_status;

        const daysLeft = Math.floor(dateService.subtractDatesV2(moment(cellData), moment(), 'day'));

        return (
          <div className="date-info-cell">
            <div>{cellData}</div>
            {daysLeft > 0 && strategyGroupStatus === 1 && (
              <div className="date-sub-label">{`${daysLeft} ${pluralize(
                'day',
                daysLeft,
              )} left`}</div>
            )}
          </div>
        );
      },
    },
  ];

  //	Table Data
  useEffect(() => {
    setSelectedColumns(
      columns.map(col => {
        return { label: col.label, value: col.dataKey };
      }),
    );
    setLoading(true);

    const tableEndpoint = `/api/v0/merchants/${merchantId}/campaigns/overview/`;
    const totalsEndpoint = `/api/v0/merchants/${merchantId}/campaigns/aggregated_overview/`;
    const params = getParams();
    const promises = [api.get(tableEndpoint, params), api.get(totalsEndpoint, params)];

    Promise.all(promises)
      .then(resp => {
        const tableData = resp[0].data.data[0];
        const totalsData = resp[1].data.data[0];

        setTableData(tableData);
        setTotalsData(totalsData);
        setLoading(false);
      })
      .catch(e => console.log(e));
  }, [searchQuery, statusFilter, selectedPubs, selectedBudgetType, dateRange, page, perPage]);

  const getParams = () => {
    const params = {
      page,
      per_page: perPage,
      status: statusFilter.length ? statusFilter.map(f => f.value).join(',') : null,
      pub_ids: selectedPubs.length ? selectedPubs.map(p => p.value).join(',') : null,
      strategy_group_types: selectedBudgetType.length
        ? selectedBudgetType.map(t => t.value).join(',')
        : null,
      filters: buildQueryFilter([
        {
          field: 'strategy_group_name',
          operation: 'contains',
          value: searchQuery,
        },
      ]),
      date_from: dateRange.startDate.format('YYYY-MM-DD'),
      date_to: dateRange.endDate.format('YYYY-MM-DD'),
    };

    return params;
  };

  const onOverflowSelect = (action, strategyGroupId) => {
    switch (action) {
      case 'edit':
        history.push(`/v2/merchant/${merchantId}/campaign-center/budgets/${strategyGroupId}/edit`);
        break;
      default:
        console.err('invalid action');
        break;
    }
  };

  const setDates = (startDate, endDate) => {
    setDateRange({
      startDate: dateService.dateFromStr(startDate),
      endDate: dateService.dateFromStr(endDate),
    });
  };

  const SummaryBar = ({ data }) => {
    if (!data) return null;

    const pacingRatio = parseFloat(data.merchant_spend_pacing) / parseFloat(data.merchant_spend);

    return (
      <div className="summary-info">
        <div className="stat">
          <span>Total Merchant Budget (this month): </span>
          <span className="bold">{`$${formatNumber(data.merch_monthly_budget, 'currency')}`}</span>
        </div>
        <span className="divider">|</span>
        <div className="stat">
          <span>Total Spend (this month): </span>
          <span className="bold">{`$${formatNumber(data.merchant_spend, 'currency')} `}</span>
          <span>Pacing towards: </span>
          <span className="pacing">
            <span className="bold">${formatNumber(data.merchant_spend_pacing, 'currency')}</span>
            <div className="pacing-icon">
              <PacingStatus value={pacingRatio} />
            </div>
          </span>
        </div>
        <span className="divider">|</span>
        <div className="stat">
          <span>
            <span className="bold">{`${data.merch_num_live_strategy_groups} `}</span>
            <span>Live Budget{data.merch_num_live_strategy_groups === 1 ? '' : 's'}, </span>
          </span>
          <span>
            <span className="bold">{`${data.merch_num_live_strategies} `}</span>
            <span>Live Campaign{data.merch_num_live_strategies === 1 ? '' : 's'}</span>
          </span>
        </div>
      </div>
    );
  };

  const TopLevelActionBar = () => {
    return (
      <div className="top-level-actions">
        <div className="left">
          <Link to="budgets/new">
            <Button iconStart="plus" label="Create Budget" size="large" />
          </Link>
          <div className="search-container">
            <Search placeholder="Search For a Budget" onSearch={setSearchQuery} debounce={true} />
          </div>
        </div>
        <div className="right">
          {/* TODO: Datepicker should close when clicking the dropdown while it's open */}
          <DatePicker
            eventPrefix="Campaign Center Budgets Tab Date Picker"
            setDateRange={setDates}
            dateRange={dateRange}
            allowFutureDates={false}
            futureLimit={dateService.getCurrentDateTime()}
          />
        </div>
      </div>
    );
  };

  const StatTiles = () => {
    return (
      <MerchantStrategyTotals
        merchId={merchantId}
        statType={statType}
        dateRange={dateRange}
        hideTargetPacing={true}
      />
    );
  };

  const getStatusFilterLabel = () => {
    if (!statusFilter.length || statusFilter.length === statusDropdownItems.length) {
      return 'All Statuses';
    }

    if (statusFilter.length === 1) {
      return statusFilter[0].label;
    }

    return `${statusFilter.length} Statuses`;
  };

  const TableLevelActionBar = data => {
    return (
      <div className="table-level-actions">
        <div className="left">
          <div className="stats-count">
            {buildCountDescription(
              data && data.page_info && data.page_info.total_items,
              'Budget',
              statusFilter,
            )}
          </div>
        </div>
        <div className="right">
          <PublisherDropdown
            selected={selectedPubs}
            onSelect={setSelectedPubs}
            confirmPrompt={true}
          />
          <DropdownSelect
            onSelect={setStatusFilter}
            selected={statusFilter}
            multi={true}
            label={getStatusFilterLabel()}
            confirmPrompt={true}
            items={statusDropdownItems}
          />
          <DropdownSelect
            onSelect={setSelectedBudgetType}
            selected={selectedBudgetType}
            multi={true}
            label={
              !selectedBudgetType.length
                ? 'Type'
                : selectedBudgetType.length > 1
                ? `${selectedBudgetType.length} Types`
                : `${selectedBudgetType[0].label}`
            }
            confirmPrompt={true}
            items={Object.keys(STRATEGY_GROUP_TYPE_LABELS).map(key => {
              return { label: STRATEGY_GROUP_TYPE_LABELS[key], value: key };
            })}
          />
          <DropdownSelect
            onSelect={setSelectedColumns}
            selected={selectedColumns}
            multi={true}
            alternateIcon="columns"
            iconSize={14}
            confirmPrompt={true}
            items={columns
              .filter(col => col.label)
              .map(col => {
                return { label: col.label, value: col.dataKey };
              })}
          />
          <ReportDropdown
            statType={statType}
            orgId={merchantId}
            gaEvent={{
              category: `Campaign Center Campaigns Tab`,
              label: 'Export Data',
            }}
            queryParams={getParams()}
            columns={selectedColumns
              .filter(col => col.label)
              .map(col => {
                return { selected: true, name: col.value };
              })}
            currentPage={page}
            perPage={perPage}
            rowCounts={{
              visible: tableData.strategy_groups && tableData.strategy_groups.length,
              all: tableData.page_info && tableData.page_info.total_items,
            }}
            disabled={loading}
            reportVersion="V2"
            useDarkIcon={true}
          />
        </div>
      </div>
    );
  };

  const CampaignsTable = ({ loading, data }) => {
    let tableContents;
    if (loading) {
      tableContents = <Loader loading={true} />;
    } else if (data.strategy_groups.length === 0) {
      tableContents = <NoResult noResult={true} type="campaign-center-budget-table" />;
    } else {
      tableContents = (
        <Table
          rowIdField="strategy_group_id"
          data={data.strategy_groups}
          visibleColumns={new Set(selectedColumns.map(col => col.value))}
          totals={totalsData.stats}
        >
          {columns.map((col, idx) => {
            return (
              <Column
                key={col.key || idx}
                dataKey={col.dataKey}
                label={col.label}
                format={col.format}
                justifyContent={col.justifyContent}
                flexWidth={col.flexWidth}
                width={col.width}
                cellRender={col.cellRender}
                totalRender={col.totalRender}
              />
            );
          })}
        </Table>
      );
    }

    const pagination = data.page_info ? (
      <div className="pager">
        <Pagination
          showPagination={true}
          currentPage={page}
          totalItems={data.page_info.total_items}
          perPage={perPage}
          updatePage={val => setPage(val)}
          updatePerPage={val => {
            setPerPage(val);
            setPage(1);
          }}
          reducedPagination={true}
        />
      </div>
    ) : null;

    return (
      <div className="stats-container">
        {TableLevelActionBar(data)}
        {tableContents}
        {pagination}
      </div>
    );
  };

  return (
    <div className="campaign-center-tab">
      {SummaryBar({ data: topBarData })}
      {TopLevelActionBar()}
      {StatTiles()}
      {CampaignsTable({ data: tableData, loading })}
    </div>
  );
};

CampaignsTab.propTypes = {};

CampaignsTab.defaultProps = {};

export default CampaignsTab;
